import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  SimpleGrid,
  Box,
  Progress,
  Avatar,
  Loader,
  Center,
  ScrollArea,
  Divider,
  Paper,
  Alert,
  ActionIcon,
  RingProgress,
  Tabs,
  Slider,
  Menu
} from '@mantine/core';
import { groupsAPI, personasAPI } from '../services/api';

const GroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [groupRecommendations, setGroupRecommendations] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationQuery, setRecommendationQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupRecipient, setGroupRecipient] = useState(null);
  const [groupPreferences, setGroupPreferences] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddPreference, setShowAddPreference] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [prefForm, setPrefForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0
  });

  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    budget_limit: '',
    gift_theme: 'general'
  });

  const [contributionForm, setContributionForm] = useState({
    persona_id: '',
    amount: '',
    note: ''
  });

  const giftThemes = [
    { value: 'general', label: '👥 General' },
    { value: 'birthday', label: '🎂 Birthday' },
    { value: 'wedding', label: '💒 Wedding' },
    { value: 'holiday', label: '🎄 Holiday' },
    { value: 'graduation', label: '🎓 Graduation' },
    { value: 'baby shower', label: '👶 Baby Shower' },
    { value: 'anniversary', label: '💖 Anniversary' },
    { value: 'secret santa', label: '🎁 Secret Santa' }
  ];

  useEffect(() => {
    loadUserData();
    
    // Check if we should auto-open create form
    if (window.location.pathname === '/groups/new') {
      setShowCreateForm(true);
    }
  }, []);

  const loadUserData = async () => {
    try {
      // Load actual groups from API
      const response = await groupsAPI.getAll();
      setGroups(response.data || []);
      setInvitations([]);
    } catch (error) {
      console.error('Failed to load group data:', error);
      // On error, show empty state
      setGroups([]);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!createFormData.name.trim()) {
      alert('Group name is required');
      return;
    }

    try {
      await groupsAPI.create(createFormData);
      setCreateFormData({ name: '', description: '', budget_limit: '', gift_theme: 'general' });
      setShowCreateForm(false);
      loadUserData();
    } catch (error) {
      alert('Failed to create group: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const loadGroupDetails = async (group) => {
    try {
      const response = await groupsAPI.getById(group.id);
      setShowGroupDetails(response.data);
      await loadContributions(group.id);
      await loadAvailablePersonas();
      await loadGroupRecipient(group.id);
      await loadGroupPreferences(group.id);
    } catch (error) {
      console.error('Failed to load group details:', error);
      setShowGroupDetails(group);
    }
  };

  const loadAvailablePersonas = async () => {
    try {
      const response = await personasAPI.getAll();
      setAvailablePersonas(response.data);
    } catch (error) {
      console.error('Failed to load personas:', error);
      // Fallback to mock data
      setAvailablePersonas([
        { id: 1, name: 'Tech-Savvy Sarah', age: 28, notes: 'Loves gadgets and smart home devices' },
        { id: 2, name: 'Book Lover Ben', age: 35, notes: 'Enjoys fantasy novels and cozy reading spots' },
        { id: 3, name: 'Foodie Frank', age: 42, notes: 'Passionate about cooking and trying new restaurants' },
        { id: 4, name: 'Fitness Fiona', age: 30, notes: 'Dedicated to health, yoga, and outdoor activities' }
      ]);
    }
  };

  const loadGroupRecipient = async (groupId) => {
    try {
      // API call to get group recipient - endpoint may need to be created
      // const response = await groupsAPI.getRecipient(groupId);
      // setGroupRecipient(response.data);
      setGroupRecipient(null); // Default to null for now
    } catch (error) {
      console.error('Failed to load group recipient:', error);
      setGroupRecipient(null);
    }
  };

  const loadGroupPreferences = async (groupId) => {
    try {
      // API call to get group preferences - using preferences API with group filter
      // const response = await preferencesAPI.getByGroup(groupId);
      // setGroupPreferences(response.data);
      setGroupPreferences([]); // Default to empty for now
    } catch (error) {
      console.error('Failed to load group preferences:', error);
      setGroupPreferences([]);
    }
  };

  const handleSetRecipient = async (persona) => {
    if (!showGroupDetails) return;
    
    try {
      // API call to set group recipient
      // await groupsAPI.setRecipient(showGroupDetails.id, persona.id);
      setGroupRecipient(persona);
    } catch (error) {
      console.error('Failed to set recipient:', error);
      alert('Failed to set recipient: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRemoveRecipient = async () => {
    if (!showGroupDetails) return;
    
    try {
      // API call to remove group recipient
      // await groupsAPI.removeRecipient(showGroupDetails.id);
      setGroupRecipient(null);
    } catch (error) {
      console.error('Failed to remove recipient:', error);
      alert('Failed to remove recipient: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleAddGroupPreference = async () => {
    if (!prefForm.tag.trim()) {
      alert('Please enter an interest or preference');
      return;
    }
    if (!showGroupDetails) return;

    try {
      // API call to create group preference
      // const response = await preferencesAPI.create({
      //   subject_type: 'group',
      //   subject_id: showGroupDetails.id,
      //   category: prefForm.category,
      //   tag: prefForm.tag,
      //   weight: prefForm.weight
      // });
      
      // For now, add to local state
      const newPref = {
        ...prefForm,
        id: Date.now(),
        addedBy: 'You',
        group_id: showGroupDetails.id
      };
      
      setGroupPreferences([...groupPreferences, newPref]);
      setPrefForm({ category: 'Technology', tag: '', weight: 0 });
      setShowAddPreference(false);
    } catch (error) {
      console.error('Failed to add group preference:', error);
      alert('Failed to add preference: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRemoveGroupPreference = async (prefId) => {
    if (!showGroupDetails) return;

    try {
      // API call to delete group preference
      // await preferencesAPI.delete(prefId);
      
      setGroupPreferences(groupPreferences.filter(p => p.id !== prefId));
    } catch (error) {
      console.error('Failed to remove group preference:', error);
      alert('Failed to remove preference: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  const loadContributions = async (groupId) => {
    try {
      const response = await groupsAPI.getContributions(groupId);
      setContributions(response.data);
    } catch (error) {
      console.error('Failed to load contributions:', error);
    }
  };

  const getThemeIcon = (theme) => {
    const icons = {
      general: '👥',
      birthday: '🎂',
      wedding: '💒',
      holiday: '🎄',
      graduation: '🎓',
      'baby shower': '👶',
      anniversary: '💖',
      'secret santa': '🎁'
    };
    return icons[theme] || '👥';
  };

  const getRoleColor = (role) => {
    const colors = {
      owner: 'red',
      admin: 'orange',
      member: 'teal'
    };
    return colors[role] || 'gray';
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading groups...</Text>
        </Stack>
      </Center>
    );
  }

  const totalBudget = groups.reduce((total, group) => total + (group.budget_limit || 0), 0);

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title 
                order={1} 
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                👥 Group Gifting
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                Collaborate with friends and family on perfect gifts
              </Text>
            </Box>
            <Button 
              size="lg" 
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={() => setShowCreateForm(true)}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.3)' }}
            >
              ✨ Create Group
            </Button>
          </Group>
        </Box>

        {/* Statistics */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb="xl">
          {[
            { value: groups.length, label: 'Active Groups', icon: '👥', color: 'blue', gradient: { from: 'blue', to: 'cyan' } },
            { value: `$${totalBudget}`, label: 'Total Budgets', icon: '💰', color: 'yellow', gradient: { from: 'yellow', to: 'orange' } },
            { value: groups.filter(g => g.budget_limit > 0).length, label: 'Goals Set', icon: '🎯', color: 'green', gradient: { from: 'teal', to: 'green' } },
            { value: invitations.length, label: 'Invitations', icon: '⏰', color: 'grape', gradient: { from: 'grape', to: 'pink' } }
          ].map((stat, index) => (
            <Paper
              key={index}
              p="xl"
              radius="lg"
              withBorder
              style={{
                background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
                borderColor: 'rgba(25,113,194,0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Stack align="center" gap="md">
                <ThemeIcon
                  size="xl"
                  radius="md"
                  variant="gradient"
                  gradient={stat.gradient}
                >
                  <Text size="xl">{stat.icon}</Text>
                </ThemeIcon>
                <Text size="48px" fw={700} lh={1}>
                  {stat.value}
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  {stat.label}
                </Text>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>

        {/* Invitations */}
        {invitations.length > 0 && (
          <Alert icon="🎁" title="Pending Invitations" color="yellow" mb="xl">
            <Stack gap="md">
              {invitations.map(inv => (
                <Card key={inv.id} p="md" withBorder>
                  <Group justify="space-between">
                    <Box>
                      <Text fw={600}>{inv.name}</Text>
                      <Text size="sm" c="dimmed">Invited by {inv.invited_by_name}</Text>
                    </Box>
                    <Group gap="xs">
                      <Button size="xs" color="teal">Accept</Button>
                      <Button size="xs" variant="outline" color="red">Decline</Button>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Groups Grid */}
        {groups.length === 0 ? (
          <Card
            p="xl"
            radius="lg"
            withBorder
            style={{
              background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
              borderStyle: 'dashed',
              borderWidth: '2px'
            }}
          >
            <Center py="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={100} radius="xl" variant="light" color="blue">
                  <Text size="60px">👥</Text>
                </ThemeIcon>
                <Title order={2} ta="center">
                  No groups yet
                </Title>
                <Text c="dimmed" ta="center" maw={500}>
                  Create your first collaborative gifting group to pool resources and find the perfect gifts together
                </Text>
                <Button 
                  size="lg" 
                  variant="gradient"
                  gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                  onClick={() => setShowCreateForm(true)}
                  mt="md"
                >
                  Create First Group
                </Button>
              </Stack>
            </Center>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            {groups.map((group) => {
              const currentContributions = Math.floor(Math.random() * (group.budget_limit || 100) * 0.8);
              const budgetProgress = group.budget_limit ? (currentContributions / group.budget_limit) * 100 : 0;

              return (
                <Card
                  key={group.id}
                  p="lg"
                  radius="lg"
                  withBorder
                  shadow="sm"
                  style={{
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Group justify="space-between" mb="md">
                    <Group gap="xs">
                      <Text size="xl">{getThemeIcon(group.gift_theme)}</Text>
                      <Text fw={700} size="lg">
                        {group.name}
                      </Text>
                    </Group>
                    <Badge color={getRoleColor(group.role)} variant="light" size="sm">
                      {group.role}
                    </Badge>
                  </Group>

                  {group.description && (
                    <Text size="sm" c="dimmed" mb="md" lineClamp={2}>
                      {group.description}
                    </Text>
                  )}

                  <SimpleGrid cols={2} spacing="sm" mb="md">
                    <Paper p="sm" radius="md" withBorder ta="center">
                      <ThemeIcon size="md" radius="xl" variant="light" color="blue" mb="xs">
                        👥
                      </ThemeIcon>
                      <Text fw={700}>{group.member_count}</Text>
                      <Text size="xs" c="dimmed">Members</Text>
                    </Paper>
                    <Paper p="sm" radius="md" withBorder ta="center">
                      <ThemeIcon size="md" radius="xl" variant="light" color="yellow" mb="xs">
                        💰
                      </ThemeIcon>
                      <Text fw={700}>${group.budget_limit || 0}</Text>
                      <Text size="xs" c="dimmed">Budget</Text>
                    </Paper>
                  </SimpleGrid>

                  {group.budget_limit && (
                    <Box mb="md">
                      <Group justify="space-between" mb="xs">
                        <Text size="xs" fw={600}>Budget Progress</Text>
                        <Text size="xs" fw={600} c={budgetProgress >= 100 ? 'teal' : 'orange'}>
                          {budgetProgress.toFixed(0)}%
                        </Text>
                      </Group>
                      <Progress 
                        value={budgetProgress} 
                        color={budgetProgress >= 100 ? 'teal' : budgetProgress >= 75 ? 'yellow' : 'blue'}
                        size="lg"
                        radius="xl"
                      />
                      <Text size="xs" c="dimmed" mt="xs">
                        ${currentContributions} / ${group.budget_limit}
                      </Text>
                    </Box>
                  )}

                  <Avatar.Group spacing="sm" mb="md">
                    {Array.from({ length: Math.min(group.member_count, 5) }, (_, i) => (
                      <Avatar
                        key={i}
                        radius="xl"
                        color={i === 0 ? 'blue' : 'gray'}
                        variant="gradient"
                        gradient={i === 0 ? { from: 'blue', to: 'cyan' } : undefined}
                      >
                        {String.fromCharCode(65 + i)}
                      </Avatar>
                    ))}
                    {group.member_count > 5 && (
                      <Avatar radius="xl">+{group.member_count - 5}</Avatar>
                    )}
                  </Avatar.Group>

                  <Button 
                    fullWidth 
                    variant="light"
                    onClick={() => loadGroupDetails(group)}
                  >
                    View Details
                  </Button>
                </Card>
              );
            })}
          </SimpleGrid>
        )}
      </Container>

      {/* Create Group Modal */}
      <Modal
        opened={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title={
          <Text size="xl" fw={700}>
            ✨ Create New Group
          </Text>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Group Name"
            placeholder="e.g., Family Holiday Gifts"
            value={createFormData.name}
            onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
            required
            size="md"
          />

          <Textarea
            label="Description"
            placeholder="What's the purpose of this group?"
            value={createFormData.description}
            onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
            rows={3}
            size="md"
          />

          <Select
            label="Gift Theme"
            data={giftThemes}
            value={createFormData.gift_theme}
            onChange={(value) => setCreateFormData({ ...createFormData, gift_theme: value })}
            size="md"
          />

          <NumberInput
            label="Budget Limit (optional)"
            placeholder="Total group budget"
            value={createFormData.budget_limit}
            onChange={(value) => setCreateFormData({ ...createFormData, budget_limit: value })}
            size="md"
            min={0}
            prefix="$"
          />

          <Group grow>
            <Button
              size="lg"
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={handleCreateGroup}
            >
              Create Group
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Group Details Modal - Enhanced with Tabs */}
      <Modal
        opened={!!showGroupDetails}
        onClose={() => {
          setShowGroupDetails(null);
          setActiveTab('overview');
          setGroupMembers([]);
          setGroupRecipient(null);
          setGroupPreferences([]);
        }}
        title={
          <Group gap="xs">
            <Text size="xl">{showGroupDetails?.gift_theme && getThemeIcon(showGroupDetails.gift_theme)}</Text>
            <Text size="xl" fw={700}>
              {showGroupDetails?.name}
            </Text>
            <Badge color={getRoleColor(showGroupDetails?.role)} variant="light">
              {showGroupDetails?.role}
            </Badge>
          </Group>
        }
        size="xl"
        centered
      >
        {showGroupDetails && (
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List grow>
              <Tabs.Tab value="overview" leftSection="📊">
                Overview
              </Tabs.Tab>
              <Tabs.Tab value="members" leftSection="👥">
                Members
              </Tabs.Tab>
              <Tabs.Tab value="recipient" leftSection="🎁">
                Recipient
              </Tabs.Tab>
              <Tabs.Tab value="preferences" leftSection="🎯">
                Preferences
              </Tabs.Tab>
              <Tabs.Tab value="contributions" leftSection="💰">
                Contributions
              </Tabs.Tab>
            </Tabs.List>

            {/* Overview Tab */}
            <Tabs.Panel value="overview" pt="md">
              <Stack gap="lg">
                {showGroupDetails.description && (
                  <Paper p="md" radius="md" withBorder>
                    <Text c="dimmed">{showGroupDetails.description}</Text>
                  </Paper>
                )}

                <SimpleGrid cols={3} spacing="md">
                  {[
                    { icon: '👥', value: showGroupDetails.member_count, label: 'Members', color: 'blue' },
                    { icon: '💰', value: `$${showGroupDetails.budget_limit || 0}`, label: 'Budget', color: 'yellow' },
                    { icon: getThemeIcon(showGroupDetails.gift_theme), value: showGroupDetails.gift_theme, label: 'Theme', color: 'teal' }
                  ].map((stat, index) => (
                    <Paper key={index} p="md" radius="md" withBorder ta="center">
                      <Text size="xl" mb="xs">{stat.icon}</Text>
                      <Text fw={700} size="lg">{stat.value}</Text>
                      <Text size="xs" c="dimmed">{stat.label}</Text>
                    </Paper>
                  ))}
                </SimpleGrid>

                <Group grow>
                  <Button variant="light" color="blue">
                    💡 Get Suggestions
                  </Button>
                  <Button variant="light" color="teal" onClick={() => setActiveTab('contributions')}>
                    ➕ Add Contribution
                  </Button>
                  <Button variant="light" color="gray" onClick={() => setActiveTab('members')}>
                    📧 Invite Member
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>

            {/* Members Tab */}
            <Tabs.Panel value="members" pt="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={600} size="lg">Group Members</Text>
                  {(showGroupDetails.role === 'owner' || showGroupDetails.role === 'admin') && (
                    <Button size="sm" onClick={() => setShowAddMember(true)}>
                      ➕ Invite Member
                    </Button>
                  )}
                </Group>

                {/* Mock members data - will be replaced with API call */}
                <Stack gap="xs">
                  {[
                    { id: 1, name: 'You', email: 'you@example.com', role: showGroupDetails.role, isCurrentUser: true },
                    { id: 2, name: 'Alice Johnson', email: 'alice@example.com', role: 'member', isCurrentUser: false },
                    { id: 3, name: 'Bob Smith', email: 'bob@example.com', role: 'member', isCurrentUser: false },
                    { id: 4, name: 'Carol Williams', email: 'carol@example.com', role: 'admin', isCurrentUser: false }
                  ].map((member) => (
                    <Paper key={member.id} p="md" withBorder radius="md">
                      <Group justify="space-between">
                        <Group gap="sm">
                          <Avatar
                            radius="xl"
                            color={member.isCurrentUser ? 'blue' : 'gray'}
                            variant={member.isCurrentUser ? 'gradient' : 'filled'}
                            gradient={member.isCurrentUser ? { from: 'blue', to: 'cyan' } : undefined}
                          >
                            {member.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Group gap="xs">
                              <Text fw={600}>{member.name}</Text>
                              {member.isCurrentUser && (
                                <Badge size="xs" color="blue" variant="light">You</Badge>
                              )}
                            </Group>
                            <Text size="sm" c="dimmed">{member.email}</Text>
                          </Box>
                        </Group>
                        <Group gap="xs">
                          <Badge color={getRoleColor(member.role)} variant="light">
                            {member.role}
                          </Badge>
                          {(showGroupDetails.role === 'owner' || showGroupDetails.role === 'admin') && !member.isCurrentUser && (
                            <Menu>
                              <Menu.Target>
                                <ActionIcon variant="subtle" color="gray">
                                  ⋮
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Label>Change Role</Menu.Label>
                                <Menu.Item onClick={() => {}}>Make Admin</Menu.Item>
                                <Menu.Item onClick={() => {}}>Make Member</Menu.Item>
                                <Menu.Divider />
                                <Menu.Item color="red" onClick={() => {}}>
                                  Remove from Group
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                        </Group>
                      </Group>
                    </Paper>
                  ))}
                </Stack>

                {/* Add Member Form */}
                {showAddMember && (
                  <Paper p="md" withBorder radius="md" style={{ background: 'rgba(25,113,194,0.05)' }}>
                    <Stack gap="sm">
                      <Text fw={600}>Invite New Member</Text>
                      <TextInput
                        placeholder="Enter email address"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        size="sm"
                      />
                      <Select
                        label="Role"
                        data={[
                          { value: 'member', label: 'Member' },
                          { value: 'admin', label: 'Admin' }
                        ]}
                        value={newMemberRole}
                        onChange={(value) => setNewMemberRole(value)}
                        size="sm"
                      />
                      <Group>
                        <Button size="sm" onClick={() => {
                          alert('Invitation sent!');
                          setNewMemberEmail('');
                          setShowAddMember(false);
                        }}>
                          Send Invitation
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setShowAddMember(false);
                          setNewMemberEmail('');
                        }}>
                          Cancel
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Tabs.Panel>

            {/* Recipient Tab */}
            <Tabs.Panel value="recipient" pt="md">
              <Stack gap="md">
                <Alert icon="ℹ️" color="blue" variant="light">
                  <Text size="sm">
                    <strong>Select a gift recipient</strong> - The recipient will not be added to the group, but their preferences will guide AI gift suggestions for all members.
                  </Text>
                </Alert>

                {groupRecipient ? (
                  <Paper p="lg" withBorder radius="md" style={{ background: 'linear-gradient(135deg, rgba(134,107,231,0.1) 0%, rgba(0,0,0,0) 100%)' }}>
                    <Group justify="space-between">
                      <Group gap="md">
                        <Avatar size="lg" radius="xl" color="grape" variant="gradient" gradient={{ from: 'grape', to: 'pink' }}>
                          <Text size="xl">🎁</Text>
                        </Avatar>
                        <Box>
                          <Group gap="xs" mb={4}>
                            <Text fw={700} size="lg">{groupRecipient.name}</Text>
                            <Badge color="grape" variant="light">Recipient</Badge>
                          </Group>
                          <Text size="sm" c="dimmed">
                            {groupRecipient.age && `Age ${groupRecipient.age} • `}
                            Gift suggestions based on their preferences
                          </Text>
                          {groupRecipient.notes && (
                            <Text size="xs" c="dimmed" mt="xs" lineClamp={2}>
                              {groupRecipient.notes}
                            </Text>
                          )}
                        </Box>
                      </Group>
                      <Button size="sm" variant="light" color="red" onClick={handleRemoveRecipient}>
                        Remove
                      </Button>
                    </Group>
                  </Paper>
                ) : (
                  <Stack gap="xs">
                    <Text fw={600} size="sm">Select from your personas:</Text>
                    <ScrollArea h={300}>
                      <Stack gap="xs">
                        {/* Mock personas - will be loaded from API */}
                        {[
                          { id: 1, name: 'Tech-Savvy Sarah', age: 28, notes: 'Loves gadgets and smart home devices' },
                          { id: 2, name: 'Book Lover Ben', age: 35, notes: 'Enjoys fantasy novels and cozy reading spots' },
                          { id: 3, name: 'Foodie Frank', age: 42, notes: 'Passionate about cooking and trying new restaurants' },
                          { id: 4, name: 'Fitness Fiona', age: 30, notes: 'Dedicated to health, yoga, and outdoor activities' }
                        ].map((persona) => (
                          <Paper
                            key={persona.id}
                            p="md"
                            withBorder
                            radius="md"
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateX(4px)';
                              e.currentTarget.style.borderColor = 'var(--mantine-color-grape-4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.borderColor = '';
                            }}
                            onClick={() => setGroupRecipient(persona)}
                          >
                            <Group gap="sm">
                              <Avatar radius="xl" color="grape">
                                {persona.name.charAt(0)}
                              </Avatar>
                              <Box style={{ flex: 1 }}>
                                <Group gap="xs">
                                  <Text fw={600}>{persona.name}</Text>
                                  {persona.age && (
                                    <Badge size="xs" variant="light">Age {persona.age}</Badge>
                                  )}
                                </Group>
                                {persona.notes && (
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {persona.notes}
                                  </Text>
                                )}
                              </Box>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </ScrollArea>
                  </Stack>
                )}
              </Stack>
            </Tabs.Panel>

            {/* Preferences Tab */}
            <Tabs.Panel value="preferences" pt="md">
              <Stack gap="md">
                <Alert icon="ℹ️" color="grape" variant="light">
                  <Text size="sm">
                    <strong>Group Preferences</strong> - Add collaborative preferences to fine-tune gift suggestions. These are specific to this group and will be removed if the group is disbanded.
                  </Text>
                </Alert>

                {/* Add Preference Form */}
                {!showAddPreference ? (
                  <Button
                    variant="light"
                    color="grape"
                    leftSection="➕"
                    onClick={() => setShowAddPreference(true)}
                  >
                    Add Group Preference
                  </Button>
                ) : (
                  <Paper p="md" withBorder radius="md" style={{ background: 'rgba(134,107,231,0.05)' }}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text fw={600}>Add New Preference</Text>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => {
                            setShowAddPreference(false);
                            setPrefForm({ category: 'Technology', tag: '', weight: 0 });
                          }}
                        >
                          ✕
                        </ActionIcon>
                      </Group>

                      <Select
                        label="Category"
                        data={[
                          'Technology', 'Food & Drink', 'Music', 'Sports & Fitness',
                          'Books & Reading', 'Movies & TV', 'Gaming', 'Fashion & Style',
                          'Travel & Adventure', 'Home & Decor', 'Pets & Animals', 'Art & Crafts',
                          'Cooking & Culinary', 'Photography', 'Outdoors & Nature', 'Wellness & Health'
                        ]}
                        value={prefForm.category}
                        onChange={(value) => setPrefForm({ ...prefForm, category: value })}
                        size="sm"
                      />

                      <TextInput
                        label="Interest/Tag"
                        placeholder="e.g., vintage vinyl records, craft beer"
                        value={prefForm.tag}
                        onChange={(e) => setPrefForm({ ...prefForm, tag: e.target.value })}
                        size="sm"
                      />

                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Preference Strength</Text>
                          <Badge
                            size="lg"
                            color={
                              prefForm.weight >= 3 ? 'teal' :
                              prefForm.weight >= 1 ? 'green' :
                              prefForm.weight <= -3 ? 'red' :
                              prefForm.weight <= -1 ? 'orange' : 'gray'
                            }
                            variant="filled"
                          >
                            {prefForm.weight > 0 ? `+${prefForm.weight}` : prefForm.weight === 0 ? '±0' : prefForm.weight}
                          </Badge>
                        </Group>
                        <Slider
                          value={prefForm.weight}
                          onChange={(value) => setPrefForm({ ...prefForm, weight: value })}
                          min={-5}
                          max={5}
                          step={1}
                          marks={[
                            { value: -5, label: '😡' },
                            { value: -3, label: '😕' },
                            { value: 0, label: '😐' },
                            { value: 3, label: '😊' },
                            { value: 5, label: '🤩' }
                          ]}
                          color={
                            prefForm.weight >= 3 ? 'teal' :
                            prefForm.weight >= 1 ? 'green' :
                            prefForm.weight <= -3 ? 'red' :
                            prefForm.weight <= -1 ? 'orange' : 'gray'
                          }
                          styles={{ markLabel: { fontSize: '16px' } }}
                        />
                        <Group justify="space-between" mt="xs">
                          <Text size="xs" c="dimmed">Avoid/Dislike</Text>
                          <Text size="xs" c="dimmed">Love/Essential</Text>
                        </Group>
                      </Box>

                      <Button
                        size="sm"
                        onClick={() => {
                          if (!prefForm.tag.trim()) {
                            alert('Please enter an interest or preference');
                            return;
                          }
                          setGroupPreferences([
                            ...groupPreferences,
                            { ...prefForm, id: Date.now(), addedBy: 'You' }
                          ]);
                          setPrefForm({ category: 'Technology', tag: '', weight: 0 });
                          setShowAddPreference(false);
                        }}
                      >
                        Add Preference
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Preferences List */}
                {groupPreferences.length > 0 ? (
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={600} size="sm">Group Preferences ({groupPreferences.length})</Text>
                      <Text size="xs" c="dimmed">Collaborative input from members</Text>
                    </Group>
                    <ScrollArea h={300}>
                      <Stack gap="xs">
                        {groupPreferences.map((pref) => (
                          <Paper key={pref.id} p="sm" withBorder radius="md">
                            <Group justify="space-between">
                              <Group gap="xs" style={{ flex: 1 }}>
                                <Badge size="xs" variant="light" color="grape">
                                  {pref.category}
                                </Badge>
                                <Text size="sm" fw={500}>{pref.tag}</Text>
                              </Group>
                              <Group gap="xs">
                                <Badge
                                  size="sm"
                                  color={
                                    pref.weight >= 3 ? 'teal' :
                                    pref.weight >= 1 ? 'green' :
                                    pref.weight <= -3 ? 'red' :
                                    pref.weight <= -1 ? 'orange' : 'gray'
                                  }
                                  variant="filled"
                                >
                                  {pref.weight > 0 ? `+${pref.weight}` : pref.weight === 0 ? '±0' : pref.weight}
                                </Badge>
                                <Text size="xs" c="dimmed">by {pref.addedBy}</Text>
                                <ActionIcon
                                  size="sm"
                                  color="red"
                                  variant="subtle"
                                  onClick={() => setGroupPreferences(groupPreferences.filter(p => p.id !== pref.id))}
                                >
                                  ✕
                                </ActionIcon>
                              </Group>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </ScrollArea>
                  </Stack>
                ) : (
                  <Center h={150}>
                    <Stack align="center" gap="xs">
                      <ThemeIcon size={50} radius="xl" variant="light" color="grape">
                        <Text size="28px">🎯</Text>
                      </ThemeIcon>
                      <Text c="dimmed" size="sm" ta="center">
                        No group preferences yet
                      </Text>
                      <Text c="dimmed" size="xs" ta="center" maw={300}>
                        Add collaborative preferences to help fine-tune gift suggestions
                      </Text>
                    </Stack>
                  </Center>
                )}
              </Stack>
            </Tabs.Panel>

            {/* Contributions Tab */}
            <Tabs.Panel value="contributions" pt="md">
              <Stack gap="md">
                {contributions?.contributions?.length > 0 ? (
                  <>
                    <Group justify="space-between">
                      <Text fw={600} size="lg">Contributions</Text>
                      <Badge color="teal">{contributions.contributions.length}</Badge>
                    </Group>
                    <ScrollArea h={300}>
                      <Stack gap="xs">
                        {contributions.contributions.map((contrib, index) => (
                          <Paper key={index} p="sm" withBorder radius="md">
                            <Group justify="space-between">
                              <Box>
                                <Text fw={600} size="sm">{contrib.persona_name || 'Unknown'}</Text>
                                <Text size="xs" c="dimmed">by {contrib.user_name}</Text>
                              </Box>
                              <Box ta="right">
                                <Text fw={700} c="teal" size="lg">${contrib.amount}</Text>
                                <Text size="xs" c="dimmed">
                                  {new Date(contrib.created_at).toLocaleDateString()}
                                </Text>
                              </Box>
                            </Group>
                            {contrib.note && (
                              <Text size="xs" c="dimmed" mt="xs" fs="italic">
                                "{contrib.note}"
                              </Text>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    </ScrollArea>

                    <Paper p="md" radius="md" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      <Text size="sm" c="white" ta="center" fw={500}>Total Contributed</Text>
                      <Text size="xl" c="white" ta="center" fw={700}>
                        ${contributions.contributions.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0).toFixed(2)}
                      </Text>
                    </Paper>
                  </>
                ) : (
                  <Center h={200}>
                    <Stack align="center" gap="md">
                      <ThemeIcon size={60} radius="xl" variant="light" color="teal">
                        <Text size="32px">💰</Text>
                      </ThemeIcon>
                      <Text c="dimmed">No contributions yet</Text>
                      <Button variant="light" color="teal">
                        Add First Contribution
                      </Button>
                    </Stack>
                  </Center>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    </Box>
  );
};

export default GroupsPage;
